// src/components/Dashboard.jsx
import React, { useRef, useState } from "react";
import "./style.css";
import { Button, Switch } from "@mui/material";
import { useDispatch } from "react-redux";
import { showSpinner, hideSpinner } from "../redux/slices/spinnerSlice";
import { useSnackbar } from "notistack";
import { submitRun } from "../utils/submitRun";

const Dashboard = () => {
  const [productType, setProductType] = useState("Volume"); // NonVolume | Volume
  const [excludeSFDC, setExcludeSFDC] = useState(false);

  const [errors, setErrors] = useState({
    ratePlanId: "",
    ratePlanChargeId: "",
    offerTemplateFile: "",
  });

  const fileRef = useRef(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const handleProductChange = (e) => setProductType(e.target.value);

  const isCsvOrXlsx = (file) => {
    if (!file) return false;
    const nameOk = /\.(xlsx|csv)$/i.test(file.name || "");
    const typeOk =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel";
    return nameOk || typeOk;
  };

  const openAndDownloadHtml = (html, filenameHint = "report.html") => {
    // Create a Blob of the HTML content
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    // 1) Open in a new tab (view the report UI)
    window.open(url, "_blank", "noopener,noreferrer");

    // 2) Download the *same* blob in parallel
    const a = document.createElement("a");
    a.href = url;
    a.download = filenameHint;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Release the object URL after a short delay
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;

    const isVolume = productType === "Volume";
    const file = fileRef.current?.files?.[0];

    try {
      setSubmitting(true);
      dispatch(
        showSpinner(
          isVolume
            ? "Generating Volume Tier Report…"
            : "Generating Non-Volume Tier Report…",
        ),
      );

      enqueueSnackbar(
        isVolume
          ? "Generating Volume Tier Report…"
          : "Generating Non-Volume Tier Report…",
        { variant: "info" },
      );

      const result = await submitRun({
        productType: isVolume ? "Volume" : "NonVolume",
        file,
        excludeSFDC,
      });

      if (result.status === 200) {
        enqueueSnackbar(
          isVolume
            ? "Volume Tier Report Generated Successfully"
            : " Non-Volume Tier Report Generated Successfully",
          { variant: "success" },
        );
      } else {
        enqueueSnackbar("Run finished with failures. Check report.", {
          variant: "warning",
        });
      }

      // *** CRITICAL: use the raw HTML returned by API, not a URL ***
      if (result.html) {
        const suggested =
          result.reportFilename ||
          `report-${isVolume ? "tier" : "non-tier"}-${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")}.html`;

        openAndDownloadHtml(result.html, suggested);
      } else {
        enqueueSnackbar("No HTML report received from API.", {
          variant: "info",
        });
      }
    } catch (err) {
      console.error("Run failed:", err);
      enqueueSnackbar(`Request failed. Error message: ${err.message}`, {
        variant: "error",
      });
    } finally {
      dispatch(hideSpinner());
      setSubmitting(false);
      handleReset();
    }
  };

  const validate = () => {
    const next = {
      offerTemplateFile: "",
    };

    const isVolume = productType === "Volume";
    const alphaNum = /^[A-Za-z0-9]+$/;

    const file = fileRef.current?.files?.[0];
    if (!file) next.offerTemplateFile = "Please upload the offer template.";
    else if (!isCsvOrXlsx(file))
      next.offerTemplateFile = "Only .xlsx or .csv is allowed.";

    setErrors(next);
    return Object.values(next).every((v) => v === "");
  };

  const handleReset = () => {
    setErrors({
      offerTemplateFile: "",
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const isVolume = productType === "Volume";

  return (
    <div className="responsive-container theme-blue">
      <div className="responsive-item">
        <form id="nsi-form" noValidate onSubmit={handleSubmit}>
          {/* Product type selector */}
          <div className="field-block">
            <div className="headings">
              Select Product Type <span className="required-asterisk">*</span>
            </div>
            <div className="select-cards">
              <label className={`select-card ${isVolume ? "active" : ""}`}>
                <input
                  type="radio"
                  name="productType"
                  value="Volume"
                  checked={isVolume}
                  onChange={handleProductChange}
                />
                <span className="select-card-label">Volume Tier</span>
              </label>
              <label className={`select-card ${!isVolume ? "active" : ""}`}>
                <input
                  type="radio"
                  name="productType"
                  value="NonVolume"
                  checked={!isVolume}
                  onChange={handleProductChange}
                />
                <span className="select-card-label">Non Volume Tier</span>
              </label>
            </div>
          </div>

          {/* File upload */}
          <div className="field-block">
            <div className="headings">
              Upload Offer Template <span className="required-asterisk">*</span>
            </div>
            <input
              type="file"
              id="offerTemplateFile"
              className="mui-like-input"
              ref={fileRef}
              accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            />
            {errors.offerTemplateFile && (
              <div
                className="error-text"
                style={{ color: "#d32f2f", fontSize: 12, marginTop: 4 }}
              >
                {errors.offerTemplateFile}
              </div>
            )}
          </div>

          {/* Exclude SFDC switch */}
          <div className="field-block">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <span style={{ fontWeight: "bold" }}>Exclude SFDC</span>

              <Switch
                checked={excludeSFDC}
                onChange={(e) => setExcludeSFDC(e.target.checked)}
                color="primary"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="btns-actions">
            <Button
              variant="contained"
              className="btn-base reset-btn"
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              className="btn-base normal-btn"
              disabled={submitting}
            >
              {submitting ? "Generating..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Dashboard;
